import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../../components/Layout';
import { useNavigate } from 'react-router-dom';
import MoveDetailView from './MoveDetailView';
import { fetchExpressMonitoring } from '../../api/salesMonitoring';
import { todayISO } from '../../utils/date';
import './SalesMonitoringResults.css';

// Suivi Express = vue live de TOUTES les ventes du jour, SEPAREES par loterie
// (pas de formulaire, pas de filtre). Un bloc par loterie ayant vendu aujourd'hui
// + le recap "Summary by Lottery" global. Rafraichi tout seul toutes les 60 s.
const REFRESH_MS = 60 * 1000;

const getTodayDisplay = () => {
  const d = new Date();
  const day = String(d.getDate()).padStart(2, '0');
  const mon = String(d.getMonth() + 1).padStart(2, '0');
  return `${day}/${mon}/${d.getFullYear()}`;
};

const money = (v) => Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });

// Bloc "squelette" affiché tant qu'aucune vente n'est encore remontée — la page
// n'est jamais vide, on voit toujours la structure des sections.
const PLACEHOLDER_BLOCK = {
  tirage_code: '', tirage_name: 'En attente de ventes…',
  numbers: [], product_summary: [], total_value: 0, total_bets: 0, placeholder: true,
};

// Meme tri que SalesMonitoringResults : Game groupe, puis Value desc, puis Number asc.
const sortNumbers = (data) => [...data].sort((a, b) => {
  if (a.game !== b.game) return a.game.localeCompare(b.game);
  if (b.value !== a.value) return b.value - a.value;
  return parseInt(a.numero, 10) - parseInt(b.numero, 10);
});

// Portail (2026-09-26) : la MEME page sert la company (configuration par defaut
// ci-dessous) et le superviseur (pages/supervisor/supervisorCentralPortal.jsx :
// SupervisorLayout, API limitee a SES banks, champ Banque superviseur).
const COMPANY_PORTAL = {
  Layout,
  centralPath: '/central',
  fetch: fetchExpressMonitoring,
  renderMoveDetail: (props) => <MoveDetailView {...props} filter="All" scopeId="" />,
};

const ExpressMonitoringPage = ({ portal = COMPANY_PORTAL }) => {
  const navigate = useNavigate();
  const rawDate = todayISO();
  const today = getTodayDisplay();

  const [selectedRow, setSelectedRow] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState('');
  const cancelledRef = useRef(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await portal.fetch({ date: rawDate });
      if (cancelledRef.current) return;
      setData(res);
      setLastUpdated(new Date().toTimeString().slice(0, 8));
    } catch {
      if (!cancelledRef.current) setError('Impossible de charger les ventes — verifie que le serveur backend tourne.');
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  }, [rawDate, portal]);

  useEffect(() => {
    cancelledRef.current = false;
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => { cancelledRef.current = true; clearInterval(id); };
  }, [load]);

  const blocks = data ? data.by_tirage : [];
  const lotterySummary = data ? data.lottery_summary : [];

  if (selectedRow) {
    return portal.renderMoveDetail({
      row: selectedRow,
      date: today,
      rawDate,
      tirage: selectedRow.tirage_code,
      tirageName: selectedRow.tirage_name,
      onBack: () => setSelectedRow(null),
    });
  }

  const grandTotal = lotterySummary.reduce((s, r) => s + r.total, 0);
  const grandLocal = lotterySummary.reduce((s, r) => s + r.local, 0);

  // On rend TOUJOURS la structure des sections — un bloc squelette si rien encore.
  const blocksToRender = blocks.length > 0 ? blocks : [PLACEHOLDER_BLOCK];

  return (
    <portal.Layout>
      <div className="smr-info-bar">
        Suivi Express — {today}
        {loading && !data ? <> · chargement…</>
          : lastUpdated ? <> · rafraichi a {lastUpdated}</> : null}
        <button className="smr-express-refresh" onClick={load}>&#8635; Rafraichir</button>
      </div>

      {error && <p className="smr-error">{error}</p>}
      {!loading && !error && blocks.length === 0 && (
        <p className="smr-express-empty">Aucune vente aujourd'hui pour l'instant.</p>
      )}

      {blocksToRender.map((block) => {
        const nums = sortNumbers(block.numbers || []);
        return (
          <div className="smr-express-block" key={block.tirage_code || 'placeholder'}>
            <div className="smr-express-head">
              <span>{block.placeholder ? block.tirage_name : `${block.tirage_code} - ${block.tirage_name}`}</span>
              <span>Total {money(block.total_value)} &nbsp;·&nbsp; {block.total_bets} paris</span>
            </div>

            <div className="smr-body">
              <div className="smr-left">
                <table className="smr-table">
                  <thead>
                    <tr>
                      <th>Game</th>
                      <th>Numeros</th>
                      <th>Value</th>
                      <th>Number of Bets</th>
                      <th>Average</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nums.length === 0 && (
                      <tr>
                        <td colSpan="5" className="smr-express-empty" style={{ textAlign: 'center' }}>
                          Aucun numéro joué pour l'instant
                        </td>
                      </tr>
                    )}
                    {nums.map((row, i) => (
                      <tr key={i}>
                        <td>{row.game}</td>
                        <td>
                          <span
                            className="smr-numero smr-numero-link"
                            onClick={() => setSelectedRow({
                              ...row,
                              tirage_code: block.tirage_code,
                              tirage_name: block.tirage_name,
                            })}
                          >{row.numero}</span>
                        </td>
                        <td>{money(row.value)}</td>
                        <td>{row.bets}</td>
                        <td>{row.avg.toFixed(2)}</td>
                      </tr>
                    ))}
                    <tr className="smr-total-row">
                      <td colSpan="2"><strong>Total</strong></td>
                      <td><strong>{money(block.total_value)}</strong></td>
                      <td><strong>{block.total_bets}</strong></td>
                      <td><strong>{block.total_bets > 0 ? (block.total_value / block.total_bets).toFixed(2) : '0.00'}</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="smr-right">
                <div className="smr-lottery-box">
                  <div className="smr-lottery-header">
                    <div className="smr-lottery-title">{block.tirage_name}</div>
                  </div>
                  <table className="smr-summary-table">
                    <tbody>
                      {(block.product_summary || []).length === 0 && (
                        <tr>
                          <td className="smr-sum-label">—</td>
                          <td className="smr-sum-value">0.00</td>
                        </tr>
                      )}
                      {(block.product_summary || []).map((row, i) => (
                        <tr key={i}>
                          <td className="smr-sum-label">{row.label}</td>
                          <td className="smr-sum-value">{money(row.value)}</td>
                        </tr>
                      ))}
                      <tr className="smr-sum-total">
                        <td>Total:</td>
                        <td className="smr-sum-value">{money(block.total_value)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      <div className="smr-bylottery-box">
        <div className="smr-bylottery-title">
          Summary by Lottery
          <button
            className="smr-back-btn"
            style={{ float: 'right' }}
            onClick={() => navigate(portal.centralPath)}
          >&#8617;</button>
        </div>
        <table className="smr-bylottery-table">
          <thead>
            <tr>
              <th>Lot</th>
              <th>Description</th>
              <th>Local</th>
              <th>Online</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {lotterySummary.length === 0 && (
              <tr>
                <td colSpan="5" className="smr-express-empty" style={{ textAlign: 'center' }}>
                  Aucune vente aujourd'hui pour l'instant
                </td>
              </tr>
            )}
            {lotterySummary.map((row, i) => (
              <tr key={i}>
                <td>{row.lot}</td>
                <td>{row.desc}</td>
                <td>{money(row.local)}</td>
                <td className="smr-online-val">{money(row.online)}</td>
                <td className="smr-online-val">{money(row.total)}</td>
              </tr>
            ))}
            <tr className="smr-bylottery-total">
              <td colSpan="2"><strong>Total</strong></td>
              <td>{money(grandLocal)}</td>
              <td className="smr-online-val">{money(grandTotal)}</td>
              <td className="smr-online-val">{money(grandTotal)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </portal.Layout>
  );
};

export default ExpressMonitoringPage;
